import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import ModalWidget from "../ModalWidget";
import { ReactComponent as ImportBorderSvg } from "../../../assets/svg/import.svg";
import { capitalizeFirstLetter } from "../../../services/HelperService";
import { importUsers } from "../../../services/UserService";
import type { ImportSummary, ImportRow } from "../../../services/UserService";
import type { ColumnMapping } from "./ImportWizard";
import type { FieldDef } from "./ImportColumnMapper";
import { UserContext } from "../../../contexts/UserContext";
import type { AccountTypeValue } from "../../../types";
import FormError from "../../FormError";
import { AccountType } from "../../../models/AccountType";
import { getBillingSubscription } from "../../../services/BillingService";
import { canInviteUserType, getInviteScope } from "../../../utils/settingsHelpers";

interface ImportPreviewProps {
    userType: string;
    rawData: string[][];
    mapping: ColumnMapping;
    manualFields?: string[];
    fields: FieldDef[];
    onBack: () => void;
    onDismiss?: () => void;
    onClose?: () => void;
    onImportComplete: (summary: ImportSummary) => void;
    lockedClassroomName?: string;
    validClassroomNames?: string[];
}

interface RowValidation {
    row: number;
    errors: string[];
    warnings: string[];
}

function validateRows(
    rows: ImportRow[],
    fields: FieldDef[],
    validClassroomNames?: string[],
    userType?: string,
): RowValidation[] {
    const issues: RowValidation[] = [];
    const requiredKeys = fields.filter(f => f.required).map(f => f.key);

    rows.forEach((row, idx) => {
        const errors: string[] = [];
        const warnings: string[] = [];
        for (const key of requiredKeys) {
            if (!row[key]?.trim()) {
                const field = fields.find(f => f.key === key);
                errors.push(`${field?.label || key} is required`);
            }
        }
        // Email format check
        if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
            errors.push("Invalid email format");
        }
        if (row.guardian_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.guardian_email)) {
            errors.push("Invalid guardian email format");
        }
        // Guardian: require at least email or phone
        if (userType === "guardian" && !row.email?.trim() && !row.phone_no?.trim()) {
            errors.push("Either email or phone number is required");
        }
        // Class name validation
        if (row.class_name?.trim() && validClassroomNames && validClassroomNames.length > 0) {
            if (!validClassroomNames.includes(row.class_name.trim().toLowerCase())) {
                warnings.push(`Class '${row.class_name.trim()}' not found`);
            }
        }
        if (errors.length > 0 || warnings.length > 0) {
            issues.push({ row: idx + 1, errors, warnings });
        }
    });
    return issues;
}

function ImportPreview({
    userType,
    rawData,
    mapping,
    manualFields = [],
    fields,
    onBack,
    onDismiss,
    onClose,
    onImportComplete,
    lockedClassroomName,
    validClassroomNames,
}: ImportPreviewProps) {
    const capitalWord = capitalizeFirstLetter(userType);
    const userCtx = useContext(UserContext);
    const schoolId = userCtx?.user?.school_id || "";
    const schoolSettings = userCtx?.user?.school?.settings;
    const isTeacherImport = userType === AccountType.TEACHER;
    const [planSendInvite, setPlanSendInvite] = useState<string[] | undefined>(undefined);
    const inviteScope = getInviteScope(planSendInvite, schoolSettings);
    const canInvite = canInviteUserType(userType, inviteScope);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sendInvite, setSendInvite] = useState(false);
    const [editedData, setEditedData] = useState<string[][]>(() => rawData.map(r => [...r]));

    // Manual field definitions
    const manualFieldDefs = useMemo(
        () => fields.filter(f => manualFields.includes(f.key)),
        [fields, manualFields]
    );

    // Manual data: row index -> field key -> value
    const [manualData, setManualData] = useState<Record<string, string>[]>(() =>
        rawData.map(() => {
            const row: Record<string, string> = {};
            for (const key of manualFields) row[key] = "";
            return row;
        })
    );

    // Track deleted rows
    const [deletedRows, setDeletedRows] = useState<Set<number>>(new Set());

    // Per-field overrides for multi-field column edits (avoids combine-then-split roundtrip)
    const [fieldOverrides, setFieldOverrides] = useState<Record<number, Record<string, string>>>({});

    const toggleDeleteRow = (rowIdx: number) => {
        setDeletedRows(prev => {
            const next = new Set(prev);
            if (next.has(rowIdx)) next.delete(rowIdx); else next.add(rowIdx);
            return next;
        });
    };

    useEffect(() => {
        if (!isTeacherImport || !schoolId) {
            setPlanSendInvite(undefined);
            return;
        }

        let isMounted = true;
        getBillingSubscription(schoolId)
            .then(sub => {
                if (isMounted) {
                    setPlanSendInvite(sub.features?.send_invite);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setPlanSendInvite(undefined);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [isTeacherImport, schoolId]);

    useEffect(() => {
        setSendInvite(canInvite);
    }, [canInvite]);

    // Build mapped field columns in order — supports multi-field columns
    const mappedCols = useMemo(() => {
        const cols: { colIdx: number; fieldKeys: string[]; fieldDefs: FieldDef[] }[] = [];
        for (const [colIdx, fieldKeys] of Object.entries(mapping)) {
            const keys = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys];
            const defs = keys.map(k => fields.find(f => f.key === k)!).filter(Boolean);
            if (defs.length > 0) {
                cols.push({ colIdx: parseInt(colIdx), fieldKeys: keys, fieldDefs: defs });
            }
        }
        return cols;
    }, [mapping, fields]);

    // Convert rawData to import rows using mapping + manual data
    const importRows: ImportRow[] = useMemo(() => {
        return editedData.map((row, rIdx) => {
            const importRow: ImportRow = {};
            for (const { colIdx, fieldKeys } of mappedCols) {
                const cellValue = row[colIdx] ?? "";
                if (fieldKeys.length === 1) {
                    importRow[fieldKeys[0]] = cellValue;
                } else {
                    // Split by space and assign in order
                    const parts = cellValue.trim().split(/\s+/);
                    fieldKeys.forEach((key, i) => {
                        importRow[key] = parts[i] ?? "";
                    });
                }
            }
            // Overlay per-field overrides for multi-field columns
            if (fieldOverrides[rIdx]) {
                for (const [key, val] of Object.entries(fieldOverrides[rIdx])) {
                    importRow[key] = val;
                }
            }
            // Merge manual fields
            if (manualData[rIdx]) {
                for (const key of manualFields) {
                    importRow[key] = manualData[rIdx][key] ?? "";
                }
            }
            // Override class_name when locked from gradebook context
            if (lockedClassroomName) {
                importRow["class_name"] = lockedClassroomName;
            }
            return importRow;
        });
    }, [editedData, mappedCols, fieldOverrides, manualData, manualFields, lockedClassroomName]);

    // Active rows (non-deleted) for display and import
    const activeRowIndices = useMemo(
        () => editedData.map((_, i) => i).filter(i => !deletedRows.has(i)),
        [editedData, deletedRows]
    );

    const activeImportRows = useMemo(
        () => activeRowIndices.map(i => importRows[i]),
        [activeRowIndices, importRows]
    );

    const validationIssues = useMemo(
        () => validateRows(activeImportRows, fields, validClassroomNames, userType),
        [activeImportRows, fields, validClassroomNames, userType]
    );

    const errorRowSet = useMemo(
        () => new Set(validationIssues.filter(v => v.errors.length > 0).map(v => v.row)),
        [validationIssues]
    );
    const warningRowSet = useMemo(
        () => new Set(validationIssues.filter(v => v.warnings.length > 0 && v.errors.length === 0).map(v => v.row)),
        [validationIssues]
    );

    const handleCellEdit = (rowIdx: number, colIdx: number, value: string) => {
        const updated = [...editedData];
        updated[rowIdx] = [...updated[rowIdx]];
        updated[rowIdx][colIdx] = value;
        setEditedData(updated);
    };

    const handleFieldEdit = (rowIdx: number, fieldKey: string, value: string) => {
        setFieldOverrides(prev => ({
            ...prev,
            [rowIdx]: { ...prev[rowIdx], [fieldKey]: value },
        }));
    };

    const handleManualEdit = (rowIdx: number, fieldKey: string, value: string) => {
        setManualData(prev => {
            const updated = [...prev];
            updated[rowIdx] = { ...updated[rowIdx], [fieldKey]: value };
            return updated;
        });
    };

    // Only rows that pass validation
    const validImportRows = useMemo(
        () => activeImportRows.filter((_, i) => !errorRowSet.has(i + 1)),
        [activeImportRows, errorRowSet]
    );

    const hasValidationErrors = validationIssues.some(v => v.errors.length > 0);

    const handleImport = async () => {
        if (validImportRows.length === 0) {
            setError("No valid rows to import. Please fix the errors above.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await importUsers(
                validImportRows,
                schoolId,
                userType as AccountTypeValue,
                isTeacherImport ? sendInvite : false,
            );
            onImportComplete(result);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            if (detail) {
                setError(detail);
            } else if (err.message === "Network Error") {
                setError("Could not reach the server. Please check your connection and try again.");
            } else {
                setError(err.message || "Import failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <ModalWidget
            headerIcon={ImportBorderSvg}
            show={true}
            onHide={onDismiss ?? onClose}
            onBack={onBack}
            title={`Preview ${capitalWord} Import`}
            description={`${validImportRows.length} of ${activeImportRows.length} row${activeImportRows.length !== 1 ? "s" : ""} ready to import.${deletedRows.size > 0 ? ` ${deletedRows.size} removed.` : ""}${hasValidationErrors ? ` Fix or remove ${validationIssues.length} row(s) with errors to continue.` : ""}`}
            actionIcon={ImportBorderSvg}
            actionTitle={loading ? "Importing..." : `Import ${validImportRows.length} row${validImportRows.length !== 1 ? "s" : ""}`}
            onAction={handleImport}
            actionDisabled={loading || validImportRows.length === 0 || hasValidationErrors}
            navigateBack={false}
            backButtonTitle="Back"
        >
            {error && (
                <FormError className="mb-3">{error}</FormError>
            )}

            <div style={{ overflowX: "auto", maxHeight: 400 }}>
                <table className="table table-sm table-bordered" style={{ fontSize: "0.8rem" }}>
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}>#</th>
                            {mappedCols.flatMap(({ fieldKeys, fieldDefs }) =>
                                fieldDefs.map((f, i) => (
                                    <th key={f.key} style={{ minWidth: 120 }}>
                                        {f.label}
                                        {f.required && <span style={{ color: "#ef4444" }}> *</span>}
                                        {fieldKeys.length > 1 && <div style={{ fontSize: "0.55rem", color: "#6b7280", fontWeight: 400 }}>from "{fieldDefs.map(fd => fd.label).join(" + ")}" col</div>}
                                    </th>
                                ))
                            )}
                            {lockedClassroomName && (
                                <th style={{ minWidth: 120, backgroundColor: "#f0fff4" }}>
                                    Class
                                    <div style={{ fontSize: "0.6rem", color: "#6b7280", fontWeight: 400 }}>auto-assigned</div>
                                </th>
                            )}
                            {manualFieldDefs.map(field => (
                                <th key={field.key} style={{ minWidth: 120, backgroundColor: "#f0f0ff" }}>
                                    {field.label}
                                    {field.required && <span style={{ color: "#ef4444" }}> *</span>}
                                    <div style={{ fontSize: "0.6rem", color: "#6b7280", fontWeight: 400 }}>manual</div>
                                </th>
                            ))}
                            <th style={{ minWidth: 120 }}>Status</th>
                            <th style={{ width: 40 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeRowIndices.map((rIdx, displayIdx) => {
                            const rowNum = displayIdx + 1;
                            const hasError = errorRowSet.has(rowNum);
                            const hasWarning = warningRowSet.has(rowNum);
                            const rowIssues = validationIssues.find(v => v.row === rowNum);
                            return (
                                <tr key={rIdx} style={{ backgroundColor: hasError ? "#fef2f2" : hasWarning ? "#fffbeb" : undefined }}>
                                    <td className="text-muted">{rowNum}</td>
                                    {mappedCols.flatMap(({ colIdx, fieldKeys }) => {
                                        const importRow = importRows[rIdx];
                                        return fieldKeys.map(key => (
                                            <td key={key}>
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    style={{ fontSize: "0.75rem", border: hasError ? "1px solid #fca5a5" : undefined }}
                                                    value={importRow[key] ?? ""}
                                                    onChange={e => {
                                                        if (fieldKeys.length > 1) {
                                                            handleFieldEdit(rIdx, key, e.target.value);
                                                        } else {
                                                            handleCellEdit(rIdx, colIdx, e.target.value);
                                                        }
                                                    }}
                                                />
                                            </td>
                                        ));
                                    })}
                                    {lockedClassroomName && (
                                        <td style={{ backgroundColor: "#f0fff4", fontSize: "0.75rem", verticalAlign: "middle" }}>
                                            {lockedClassroomName}
                                        </td>
                                    )}
                                    {manualFieldDefs.map(field => (
                                        <td key={field.key} style={{ backgroundColor: "#f8f8ff" }}>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                style={{ fontSize: "0.75rem", backgroundColor: "#f8f8ff", border: hasError ? "1px solid #fca5a5" : "1px solid #c7c7f0" }}
                                                placeholder={field.label}
                                                value={manualData[rIdx]?.[field.key] ?? ""}
                                                onChange={e => handleManualEdit(rIdx, field.key, e.target.value)}
                                            />
                                        </td>
                                    ))}
                                    <td>
                                        {hasError ? (
                                            <span className="text-danger font-xs">
                                                {rowIssues?.errors.join("; ")}
                                            </span>
                                        ) : hasWarning ? (
                                            <span style={{ color: "#d97706" }} className="font-xs">
                                                OK — {rowIssues?.warnings.join("; ")}
                                            </span>
                                        ) : (
                                            <span className="text-success font-xs">OK</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm p-0"
                                            title="Remove row"
                                            onClick={() => toggleDeleteRow(rIdx)}
                                            style={{ color: "#dc2626", fontSize: "1.1rem", fontWeight: 700, lineHeight: 1, width: 24, height: 24 }}
                                        >
                                            &times;
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {isTeacherImport && (
                <div className="mt-3">
                    {!canInvite ? (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-invite-import">Your current plan does not support inviting teachers</Tooltip>}
                        >
                            <div style={{ display: 'inline-flex' }}>
                                <Form.Check type="switch" id="send-invite-import" label="Send invite emails to imported teachers" checked={false} disabled />
                            </div>
                        </OverlayTrigger>
                    ) : (
                        <Form.Check
                            type="switch"
                            id="send-invite-import"
                            label="Send invite emails to imported teachers"
                            checked={sendInvite}
                            onChange={e => setSendInvite(e.target.checked)}
                        />
                    )}
                </div>
            )}
        </ModalWidget>
    );
}

export default ImportPreview;
